/**
 * Upload Context
 * Provides a globally accessible sequential upload queue.
 *
 * Uploads execute one at a time; consumers on any page can read
 * queue state to show inline progress rows (models page) or a
 * corner toast (other pages).  Closing the upload modal does NOT
 * cancel an in-progress upload.
 */

import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useQueryClient } from '@tanstack/react-query';
import { modelsService } from '../services';
import { queryKeys } from '../lib/queryClient';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type UploadItemStatus = 'queued' | 'uploading' | 'processing' | 'complete' | 'error';

export interface UploadQueueItem {
  id: string;
  file: File;
  /** Copy of file.name captured at enqueue time */
  name: string;
  /** Copy of file.size captured at enqueue time */
  size: number;
  status: UploadItemStatus;
  /** 0–100 byte-upload progress; set to 100 once processing begins */
  progress: number;
  /** Set once the API returns a created model id */
  modelId?: string;
  /** Set when status is 'error' */
  error?: string;
}

export interface UploadContextValue {
  queue: UploadQueueItem[];
  activeUploadId: string | null;
  /** Adds a file to the queue and returns the local item id */
  enqueueUpload: (file: File) => string;
  /** Aborts an active upload or removes a queued/complete/error item */
  cancelUpload: (id: string) => void;
  /** Removes all 'complete' and 'error' items from the queue */
  dismissCompleted: () => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const UploadContext = createContext<UploadContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const UploadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();

  const [queue, setQueue] = useState<UploadQueueItem[]>([]);
  const [activeUploadId, setActiveUploadId] = useState<string | null>(null);

  /** AbortController for the currently active upload */
  const abortRef = useRef<AbortController | null>(null);
  /** Mirrors activeUploadId so callbacks avoid stale closure captures */
  const activeUploadIdRef = useRef<string | null>(null);

  /** Keep both state and ref in sync */
  const setActive = (id: string | null) => {
    activeUploadIdRef.current = id;
    setActiveUploadId(id);
  };

  // ---------------------------------------------------------------------------
  // Queue helpers
  // ---------------------------------------------------------------------------

  const updateItem = (id: string, patch: Partial<UploadQueueItem>) =>
    setQueue(prev => prev.map(i => (i.id === id ? { ...i, ...patch } : i)));

  const removeItem = (id: string) =>
    setQueue(prev => prev.filter(i => i.id !== id));

  // ---------------------------------------------------------------------------
  // Upload function — stored in a ref so the promotion effect always
  // calls the latest version and avoids stale closures.
  // ---------------------------------------------------------------------------

  const runUploadFnRef = useRef<(id: string, file: File, signal: AbortSignal) => Promise<void>>();

  runUploadFnRef.current = async (id: string, file: File, signal: AbortSignal) => {
    try {
      updateItem(id, { status: 'uploading', progress: 0 });

      const model = await modelsService.uploadModel(
        file,
        (p) => { updateItem(id, { progress: p }); },
        signal,
      );

      updateItem(id, { status: 'processing', progress: 100, modelId: model.id });

      const finalModel = await modelsService.pollModelStatus(model.id, 2000, 60, signal);

      if (finalModel.status === 'ready') {
        updateItem(id, { status: 'complete' });
        queryClient.invalidateQueries({ queryKey: queryKeys.models.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.models.storageUsage });
      } else {
        updateItem(id, {
          status: 'error',
          error: finalModel.processingError ?? 'Processing failed',
        });
      }
    } catch (e: unknown) {
      if (e instanceof Error && e.name === 'AbortError') {
        // Cancelled by the user — remove item silently
        removeItem(id);
      } else {
        const msg = e instanceof Error ? e.message : 'Upload failed';
        updateItem(id, { status: 'error', error: msg });
      }
    } finally {
      setActive(null);
      abortRef.current = null;
    }
  };

  // ---------------------------------------------------------------------------
  // Promotion loop — when nothing is active, start the next queued item
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (activeUploadId !== null) return;
    const next = queue.find(i => i.status === 'queued');
    if (!next) return;

    const controller = new AbortController();
    abortRef.current = controller;
    setActive(next.id);
    runUploadFnRef.current?.(next.id, next.file, controller.signal);
  }, [activeUploadId, queue]);

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  const enqueueUpload = useCallback((file: File): string => {
    const id = uuidv4();
    setQueue(prev => [
      ...prev,
      { id, file, name: file.name, size: file.size, status: 'queued', progress: 0 },
    ]);
    return id;
  }, []);

  /**
   * Abort the active upload or remove a queued / finished item.
   * Uses refs so the callback is stable with an empty dependency array.
   */
  const cancelUpload = useCallback((id: string) => {
    if (id === activeUploadIdRef.current) {
      // The catch block in runUpload handles item removal after abort
      abortRef.current?.abort();
    } else {
      removeItem(id);
    }
  }, []); // intentionally no deps — reads from refs

  const dismissCompleted = useCallback(() => {
    setQueue(prev => prev.filter(i => i.status !== 'complete' && i.status !== 'error'));
  }, []);

  return (
    <UploadContext.Provider
      value={{ queue, activeUploadId, enqueueUpload, cancelUpload, dismissCompleted }}
    >
      {children}
    </UploadContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useUploadContext(): UploadContextValue {
  const ctx = useContext(UploadContext);
  if (!ctx) throw new Error('useUploadContext must be used inside UploadProvider');
  return ctx;
}
